import { useCallback, type Dispatch, type SetStateAction } from 'react'
import { emporionToast as toast } from '@admin/toast'
import type { useI18n } from '../../../i18n'
import type { AdminAccount } from '../../../types'
import {
  requestCurrentPasswordVerify,
  requestPasswordChange,
} from '../passwordApi'
import type { AdminHeadersGetter } from './types'
import { runAsyncAction } from '../asyncActionFeedback'

type Params = {
  token: string | null
  authHeaders: AdminHeadersGetter
  t: ReturnType<typeof useI18n>['t']
  setAccount: Dispatch<SetStateAction<AdminAccount | null>>
  ensureAuthenticated: () => boolean
}

export const useProfileSecurityPasswordActions = ({
  token,
  authHeaders,
  t,
  setAccount,
  ensureAuthenticated,
}: Params) => {
  const changePassword = useCallback(
    async (input: { currentPassword: string; newPassword: string; otpCode?: string; recoveryCode?: string }) => {
      if (!ensureAuthenticated()) return false
      const data = await runAsyncAction({
        action: () => requestPasswordChange({ authHeaders, input, t }),
        fallbackErrorMessage: t('profile.toast.passwordError'),
        onErrorMessage: (message) => toast.error(message),
      })
      if (!data) return false

      if (data.account) {
        setAccount(data.account as AdminAccount)
      }
      toast.success(t('profile.toast.passwordUpdated'))
      return true
    },
    [authHeaders, ensureAuthenticated, setAccount, t],
  )

  const verifyCurrentPassword = useCallback(
    async (currentPassword: string) => {
      if (!token) return false
      try {
        return await requestCurrentPasswordVerify({ authHeaders, currentPassword, t })
      } catch {
        return false
      }
    },
    [authHeaders, t, token],
  )

  return {
    changePassword,
    verifyCurrentPassword,
  }
}
